import { motion } from 'framer-motion';
import { Cpu, Zap, Settings, Shield, Layers, Circle } from 'lucide-react';

const Manufacturing = () => {
  const machinery = [
    {
      icon: Cpu,
      title: 'ATC CNC Machines',
      description: 'Automated Tool Change CNC for precision cutting and routing of panels with micron-level accuracy.',
      image: 'https://images.pexels.com/photos/10119312/pexels-photo-10119312.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2'
    },
    {
      icon: Settings,
      title: 'Panel Saw',
      description: 'High-capacity panel saw for efficient sizing and cutting of large material sheets.',
      image: 'https://images.pexels.com/photos/10119312/pexels-photo-10119312.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2'
    },
    {
      icon: Layers,
      title: 'Automatic Edge Bander',
      description: 'Seamless edge banding with perfect adhesion for premium finish quality.',
      image: 'https://images.pexels.com/photos/10119312/pexels-photo-10119312.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2'
    },
    {
      icon: Circle,
      title: 'PU Painting Chamber',
      description: 'State-of-the-art polyurethane painting chamber for mirror-finish coating and protection.',
      image: 'https://images.pexels.com/photos/10119312/pexels-photo-10119312.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2'
    }
  ];

  const process = [
    { step: '01', title: 'Design & CAD', description: 'Client requirements converted to precise technical drawings' },
    { step: '02', title: 'Material Selection', description: 'Premium grade materials sourced and quality checked' },
    { step: '03', title: 'CNC Cutting', description: 'Automated precision cutting with zero tolerance' },
    { step: '04', title: 'Edge Banding', description: 'Seamless edge application for durability' },
    { step: '05', title: 'Assembly', description: 'Expert assembly with quality hardware' },
    { step: '06', title: 'PU Finishing', description: 'Multi-layer coating for lasting protection' },
    { step: '07', title: 'Quality Check', description: 'Rigorous inspection before dispatch' },
    { step: '08', title: 'Packaging & Delivery', description: 'Secure packaging and timely delivery' }
  ];

  return (
    <div className="min-h-screen pt-20" data-testid="manufacturing-page">
      {/* Hero */}
      <section className="py-24 md:py-32 bg-slate-50" data-testid="manufacturing-hero">
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-4">Manufacturing Facility</p>
            <h1 className="text-5xl md:text-7xl font-bold tracking-tight leading-none mb-6">
              Automated Precision.<br />
              Zero Compromise.
            </h1>
            <p className="text-lg md:text-xl leading-relaxed text-muted-foreground max-w-3xl">
              Our state-of-the-art facility combines advanced CNC technology, automated processes, and skilled 
              craftsmanship to deliver furniture of unmatched quality.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Machinery Showcase */}
      <section className="py-24 md:py-32" data-testid="machinery-section">
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center mb-16">
            <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-4">Technology & Equipment</p>
            <h2 className="text-3xl md:text-5xl font-semibold tracking-tight">
              Industrial-Grade Machinery
            </h2>
          </div>

          <div className="space-y-24">
            {machinery.map((machine, index) => (
              <motion.div
                key={machine.title}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
                className={`grid grid-cols-1 lg:grid-cols-2 gap-16 items-center ${
                  index % 2 === 1 ? 'lg:flex-row-reverse' : ''
                }`}
                data-testid={`machine-${index}`}
              >
                <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-slate-50 mb-6">
                    <machine.icon className="w-8 h-8 text-slate-900" />
                  </div>
                  <h3 className="text-3xl md:text-4xl font-semibold tracking-tight mb-4">{machine.title}</h3>
                  <p className="text-base leading-relaxed text-muted-foreground">{machine.description}</p>
                </div>
                <div className={index % 2 === 1 ? 'lg:order-1' : ''}>
                  <div className="relative h-96 bg-slate-100">
                    <img 
                      src={machine.image} 
                      alt={machine.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Flow */}
      <section className="py-24 md:py-32 bg-slate-900 text-white" data-testid="process-section">
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center mb-16">
            <p className="text-xs font-bold uppercase tracking-widest text-bronze-500 mb-4">Our Process</p>
            <h2 className="text-3xl md:text-5xl font-semibold tracking-tight">
              From Concept to Completion
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {process.map((item, index) => (
              <motion.div
                key={item.step}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className="relative"
                data-testid={`process-${index}`}
              >
                <div className="text-6xl font-bold text-bronze-500/20 mb-4">{item.step}</div>
                <h3 className="text-lg font-medium mb-2">{item.title}</h3>
                <p className="text-white/70 text-sm leading-relaxed">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Quality Standards */}
      <section className="py-24 md:py-32" data-testid="quality-section">
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <Shield className="w-16 h-16 text-bronze-500 mb-6" />
              <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-4">Quality Control</p>
              <h2 className="text-3xl md:text-5xl font-semibold tracking-tight mb-6">
                Precision in Every Measurement
              </h2>
              <p className="text-base leading-relaxed text-muted-foreground mb-6">
                Every piece of furniture that leaves our facility undergoes rigorous quality inspection. From 
                material selection to final assembly, we maintain strict tolerances and finishing standards.
              </p>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <Zap className="w-5 h-5 text-bronze-500 flex-shrink-0" />
                  <span className="text-muted-foreground">Multi-point dimensional verification</span>
                </div>
                <div className="flex items-center gap-3">
                  <Zap className="w-5 h-5 text-bronze-500 flex-shrink-0" />
                  <span className="text-muted-foreground">Surface finish inspection</span>
                </div>
                <div className="flex items-center gap-3">
                  <Zap className="w-5 h-5 text-bronze-500 flex-shrink-0" />
                  <span className="text-muted-foreground">Hardware functionality testing</span>
                </div>
                <div className="flex items-center gap-3">
                  <Zap className="w-5 h-5 text-bronze-500 flex-shrink-0" />
                  <span className="text-muted-foreground">Final product documentation</span>
                </div>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative h-96 lg:h-full bg-slate-100"
            >
              <img 
                src="https://images.pexels.com/photos/35383622/pexels-photo-35383622.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2" 
                alt="Quality Control"
                className="w-full h-full object-cover"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Factory Stats */}
      <section className="py-24 bg-slate-50" data-testid="factory-stats">
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { value: '25,000', label: 'Sq Ft Facility', suffix: '' },
              { value: '50', label: 'Skilled Workers', suffix: '+' },
              { value: '1000', label: 'Units/Month', suffix: '+' },
              { value: '99.8', label: 'Quality Rate', suffix: '%' }
            ].map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center"
                data-testid={`factory-stat-${index}`}
              >
                <div className="text-4xl md:text-5xl font-bold text-slate-900 mb-2">
                  {stat.value}{stat.suffix}
                </div>
                <div className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                  {stat.label}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Manufacturing;
